import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';

/** `/version.json`, written at deploy by scripts/generate-version.js. */
interface DeployedVersion {
  commit?: string | null;
  main?: string | null;
}

/** Which kind of client a user runs — one `users/{uid}.clients.<surface>` entry each. */
export type ClientSurface = 'web' | 'desktop' | 'mobile';

/** `users/{uid}.clients.<surface>` (plus `at`, a server timestamp). */
export interface ClientRecord {
  /** Commit of the running web build, or null when it is not the latest deploy / unknown. */
  web: string | null;
  /** The running entry bundle (`main-<hash>.js`) — identifies the build even when `web` is null. */
  webBundle: string | null;
  /** Running an older build than the latest deploy (stale service-worker copy). */
  stale: boolean;
  /** Desktop app version (desktop only; null on shells older than 1.0.12). */
  desktop: string | null;
  os: 'windows' | 'mac' | 'linux' | 'ios' | 'android' | 'other';
}

/** Rewrite an unchanged record at most this often, so `at` stays a rough "last used". */
const RECORD_REFRESH_MS = 24 * 60 * 60 * 1000;

/**
 * Which build this page is running, for the account menu's version line (support,
 * 2026-11-03: "it would be nice to have a version shown somewhere").
 *
 * The bundle carries no commit, and `/version.json` describes the LATEST deploy, not
 * necessarily the copy this page runs (the service worker can serve an older one).
 * So the running build is identified by its own entry bundle (`main-<hash>.js` in
 * the DOM) and matched against the `main` the deploy recorded:
 *   - same bundle → "web 548d37c" (the deploy's commit);
 *   - different   → "web INVJNEGY" + `stale` — an older build is running, which is
 *                   itself the answer to many "it broke after the update" reports.
 *
 * In the desktop app the shell's version comes first ("Crewdle 1.0.12 · …", from
 * `crewdleNative.appVersion`; older shells do not expose it).
 */
@Injectable({ providedIn: 'root' })
export class BuildInfoService {
  /** Short label, e.g. "Crewdle 1.0.12 · web 548d37c". */
  readonly label: Signal<string>;

  /** True when the page runs an older build than the latest deploy. */
  readonly stale: Signal<boolean>;

  private readonly firebase = inject(FirebaseService);
  private readonly deployed = signal<DeployedVersion | null>(null);
  private readonly loaded: Promise<void>;
  private readonly runningMain = runningEntryBundle();
  private readonly native = (window as unknown as { crewdleNative?: { appVersion?: string | null } }).crewdleNative;

  constructor() {
    this.stale = computed(() => {
      const main = this.deployed()?.main;
      return !!main && !!this.runningMain && main !== this.runningMain;
    });
    this.label = computed(() => [this.desktopPart(), `web ${this.webPart()}`].filter(Boolean).join(' · '));
    this.loaded = this.load();
  }

  /**
   * Record this client on the user's doc (`clients.web|desktop|mobile`), so support can
   * see which versions a person runs without asking. Only versions and the OS family:
   * user docs are readable by any signed-in user (firestore.rules `users/{userId}`).
   * Written when the record changes, otherwise at most once a day.
   */
  async recordClient(uid: string): Promise<void> {
    await this.loaded;
    const surface = this.surface();
    const record: ClientRecord = {
      web: this.webCommit(),
      webBundle: this.runningMain,
      stale: this.stale(),
      desktop: surface === 'desktop' ? (this.native?.appVersion ?? null) : null,
      os: osFamily(),
    };
    const key = `crewdle:client-record:${uid}:${surface}`;
    const fingerprint = JSON.stringify(record);
    try {
      const last = JSON.parse(localStorage.getItem(key) ?? 'null') as { fingerprint: string; at: number } | null;
      if (last?.fingerprint === fingerprint && Date.now() - last.at < RECORD_REFRESH_MS) return;
    } catch {
      // unreadable marker → write
    }
    try {
      await this.firebase.globalFirestore.docUpdate(`users/${uid}`, {
        [`clients.${surface}`]: { ...record, at: this.firebase.firestore.serverTimestamp() },
      });
      localStorage.setItem(key, JSON.stringify({ fingerprint, at: Date.now() }));
    } catch {
      // Best effort: support info only.
    }
  }

  private surface(): ClientSurface {
    if ((window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.()) return 'mobile';
    return this.native ? 'desktop' : 'web';
  }

  private webCommit(): string | null {
    const deployed = this.deployed();
    return deployed?.commit && deployed.main && deployed.main === this.runningMain ? deployed.commit.slice(0, 7) : null;
  }

  /** Multi-line details for a support message (copied by the menu item). */
  details(orgId: string | null): string {
    const deployed = this.deployed();
    return [
      `Version: ${this.label()}${this.stale() ? ' (older than the latest deploy)' : ''}`,
      `Web build: ${this.runningMain ?? 'unknown'}${deployed?.commit ? ` · latest deploy ${deployed.commit.slice(0, 7)} (${deployed.main ?? '?'})` : ''}`,
      `Desktop app: ${this.native ? (this.native.appVersion ?? 'older version (no version reported)') : 'no (browser)'}`,
      `Browser: ${navigator.userAgent}`,
      `Organization: ${orgId ?? '-'}`,
      `Time: ${new Date().toISOString()}`,
    ].join('\n');
  }

  private desktopPart(): string {
    if (!this.native) return '';
    return this.native.appVersion ? `Crewdle ${this.native.appVersion}` : 'Crewdle desktop';
  }

  private webPart(): string {
    // Unknown deploy, or an older build: the running bundle's hash still identifies it.
    return this.webCommit() ?? this.runningMain?.replace(/^main-|\.js$/g, '') ?? 'dev';
  }

  private async load(): Promise<void> {
    try {
      const res = await fetch('/version.json', { cache: 'no-store' });
      if (res.ok) this.deployed.set((await res.json()) as DeployedVersion);
    } catch {
      // Offline or `ng serve` (no version.json): the bundle hash alone is shown.
    }
  }
}

function runningEntryBundle(): string | null {
  for (const script of Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))) {
    const name = script.src.split('/').pop()?.split('?')[0] ?? '';
    if (/^main-[A-Za-z0-9_-]+\.js$/.test(name)) return name;
  }
  return null;
}

function osFamily(): ClientRecord['os'] {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Windows/.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/.test(ua)) return 'mac';
  if (/Linux|X11/.test(ua)) return 'linux';
  return 'other';
}
